import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode, type RefObject,
} from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import type { AssistantObjectRef, AssistantMeDTO, AssistantTurnContext } from '@contracts/assistant';
import type { AppOpenBubbleDTO } from '@contracts/assistant-proactive';
import type { IngestJobDTO } from '@contracts/ingest';
import type { Id, Principal } from '@contracts/common';
import { MODAL_OPEN, type AssistantSlot } from '../../components/layout/app-shell';
import { publishWorkplaceFocus, readWorkplaceFocus } from '../workplace/focus';
import { useNarrowView } from '../../lib/media';
import { HANA_CONVERSATION_KEY } from '../../lib/person-storage';
import { useMe } from './api';
import { useAppOpen } from './proactive/api';
import { bubbleSeen } from './proactive/HanaBubble';
import { AssistantButton } from './AssistantButton';
import { AssistantPanel } from './AssistantPanel';
import type { Emotion, EmotionInput } from './emotion';
import { glowObjects } from './glow';
import { refreshFor } from './refresh';
import { useHanaUndo, type HanaUndo } from './useHanaUndo';
import { useEmotion } from './useEmotion';
import { prefersReducedMotion } from './useTurn';

export const PANEL_STORAGE_KEY = 'hana:panel';
export const CONVERSATION_STORAGE_KEY = HANA_CONVERSATION_KEY;
/** The panel's id: the button's `aria-controls`. */
export const PANEL_ID = 'tro-ly-hana';

export type AssistantUi = {
  prefs: AssistantMeDTO; principal: Principal;
  open: boolean; setOpen: (open: boolean) => void; toggle: () => void;
  conversationId: Id | null; setConversationId: (id: Id | null) => void;
  /**
   * Opens the panel on this conversation (the "(qua Hana)" link, D169). `opener` gets the focus back when the panel
   * closes (R13); without it, the header button does.
   */
  openConversation: (id: Id, opener?: HTMLElement | null) => void;
  /** Counts `openConversation` switches, so an open panel leaves its live turn behind. */
  switchSeq: number;
  emotion: Emotion; feel: (input: EmotionInput) => void; still: boolean;
  /** The header button, so closing the panel hands the focus back to it. */
  buttonRef: RefObject<HTMLButtonElement | null>;
  /** Hana's undo offers: here, not in the panel, so the toast and ⌘Z outlive closing it. */
  hanaUndo: HanaUndo;
  /** Shows on the page what Hana just touched (D88): now, or once the phone sheet no longer covers the page (D145). */
  glow: (refs: readonly AssistantObjectRef[]) => void;
  /**
   * D208: a fixed command about one object (the Map card menu's ✦ Hana items). Opens the panel, makes that card the
   * Workplace's focus and sends the text with it as the page's object. Only text and the ref go: the server checks the
   * ref like any other page object (§6.1), so this never widens what Hana may touch.
   * With a `context` instead (S3 D128, "Hỏi Hana thêm" on the briefing card), the text opens a NEW conversation whose
   * first turn carries that context; the server checks it (owner, projection) like the nudge's.
   * With neither (S8 D137, a step-6 command on `/welcome`), the text goes as the person typed it, about the page.
   */
  ask: (text: string, about?: AssistantObjectRef | { context: AssistantTurnContext }) => void;
  /** Counts `ask` calls: the panel sends what `takeAsked` hands it once per change. */
  asked: number;
  /** The command `ask` left for the panel, handed over once (null when there is none). */
  takeAsked: () => AssistantAsk | null;
  /**
   * L4 D160: files dropped on the Map (onto a card: `anchorItemId`) for Hana to read. Opens the panel, whose ingest
   * input takes them once (`takeDrops`); the caller checks that ingest is usable (D114, D132).
   */
  dropFiles: (files: readonly File[], opts?: { anchorItemId?: Id }) => void;
  /** Counts `dropFiles` calls: the panel takes what `takeDrops` hands it once per change. */
  dropped: number;
  takeDrops: () => AssistantDrop[];
  /**
   * L4 D168: the last job a Map drop started (`seq` counts them): the Map, while it is on screen, shows that batch
   * (`?batch`). Set by the panel through `ingestedFromMap`.
   */
  mapIngest: { jobId: Id; seq: number } | null;
  ingestedFromMap: (jobId: Id) => void;
  /** L4 D160: "Xem trong panel" on the Map's batch bar — opens the panel at the batch's card (D120's jump). */
  showBatch: (job: IngestJobDTO) => void;
  /** Counts `showBatch` calls; `takeShown` hands the batch over once (null when there is none). */
  shownBatch: number;
  takeShown: () => IngestJobDTO | null;
  /** S7 §9.4 (D102): a run notice opens the panel on its chat view, where the tray is. */
  showTray: () => void;
  /** Counts `showTray` calls: the panel goes back to its chat view on each change. */
  trayShown: number;
  /** L3 D124: this mount's `POST /app-open` has answered (or failed); the briefing card reads after it. */
  appOpenSettled: boolean;
  /** L3 D123: the opening bubble this mount's app-open was granted (null when none). */
  appOpenBubble: AppOpenBubbleDTO | null;
};

export type AssistantAsk =
  | { text: string; objectRef: AssistantObjectRef; context?: undefined }
  | { text: string; objectRef?: undefined; context: AssistantTurnContext }
  | { text: string; objectRef?: undefined; context?: undefined };

/**
 * S8 D137: the router state a step-6 command crosses `/welcome` → `/` in. `/welcome` has its own provider and no panel,
 * so the app's provider (mounted by that navigation) takes the text once and asks it.
 */
export const askOnArrival = (text: string): { assistantAsk: string } => ({ assistantAsk: text });

/** One Map drop waiting for the panel (D160): its files, and the card it was dropped on. */
export type AssistantDrop = { files: File[]; anchorItemId?: Id };

const AssistantContext = createContext<AssistantUi | null>(null);

export function useAssistant(): AssistantUi {
  const ui = useContext(AssistantContext);
  if (ui === null) throw new Error('useAssistant is used outside AssistantProvider');
  return ui;
}

/** The assistant's state, or null where Hana is not usable (AI off, no model) or outside the shell. */
export function useAssistantOptional(): AssistantUi | null {
  return useContext(AssistantContext);
}

/** Every storage call may throw (a locked-down browser): the panel then simply forgets on reload. */
function readStored(key: string): string | null {
  try { return window.sessionStorage.getItem(key); } catch { return null; }
}
function writeStored(key: string, value: string | null): void {
  try {
    if (value === null) window.sessionStorage.removeItem(key);
    else window.sessionStorage.setItem(key, value);
  } catch { /* the tab forgets, nothing more */ }
}

/**
 * Hana's state for the whole shell (spec §8.1): whether the panel is open, which conversation it shows, and the one
 * face the header button and the panel share. Renders `children(slot)`; `slot` is undefined while AI is not usable for
 * this person (D125), so the header shows no button at all.
 */
export function AssistantProvider({ principal, children }: {
  principal: Principal; children: (slot: AssistantSlot | undefined) => ReactNode;
}) {
  const { data: prefs } = useMe();
  const usable = prefs != null && prefs.models.length > 0;
  const [open, setOpenState] = useState(() => readStored(PANEL_STORAGE_KEY) === '1');
  const [conversationId, setConversationId] = useState<Id | null>(() => readStored(CONVERSATION_STORAGE_KEY));
  const { emotion, feel } = useEmotion({ open });
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const narrow = useNarrowView();
  const location = useLocation();
  const { pathname } = location;
  // D122 (§8.5): the bubble is asked for only where it can show — not on a phone, not on the Dashboard (the card is
  // there), not over the open panel, not on the first-run wizard (S8 §8.1: nothing of the app is on screen there),
  // and not once this session has had its one bubble (the server would spend the day's greeting on a bubble the
  // session key then hides).
  const appOpen = useAppOpen(prefs, () =>
    !narrow && pathname !== '/' && pathname !== '/welcome' && !open && !bubbleSeen(principal.userId));
  const appOpenSettled = appOpen.settled;
  const appOpenBubble = appOpen.bubble;
  const queryClient = useQueryClient();

  // D145: below lg the sheet covers the page, so the glow waits for it to close (or for a chip to navigate away).
  const deferred = useRef<AssistantObjectRef[]>([]);
  const reduced = (prefs?.reducedMotion ?? false) || prefersReducedMotion();
  const glow = useCallback((refs: readonly AssistantObjectRef[]) => {
    if (narrow && open) { deferred.current.push(...refs); return; }
    glowObjects(refs, { reduced });
  }, [narrow, open, reduced]);
  useEffect(() => {
    if ((narrow && open) || deferred.current.length === 0) return;
    const refs = deferred.current;
    deferred.current = [];
    glowObjects(refs, { reduced });
  }, [narrow, open, reduced]);
  const glowRef = useRef(glow);
  useEffect(() => { glowRef.current = glow; });

  // An undo refreshes and glows what it put back, the same way a step does.
  const hanaUndo = useHanaUndo({
    onUndone: (objects) => { void refreshFor(queryClient, objects).then(() => glowRef.current(objects)); },
  });

  useEffect(() => { writeStored(PANEL_STORAGE_KEY, open ? '1' : null); }, [open]);
  useEffect(() => { writeStored(CONVERSATION_STORAGE_KEY, conversationId); }, [conversationId]);

  // D208: the command waits here until the panel (mounted by the open) takes it; a second ask replaces an untaken one.
  const waiting = useRef<AssistantAsk | null>(null);
  // P4 G8 re-review N2: a command held behind a running turn goes with the panel; reopening it later sends nothing.
  const setOpen = useCallback((next: boolean) => {
    if (!next) waiting.current = null;
    setOpenState((was) => {
      if (next && !was) feel({ kind: 'opened' });
      return next;
    });
  }, [feel]);
  const toggle = useCallback(() => {
    setOpenState((was) => {
      if (was) waiting.current = null;
      if (!was) feel({ kind: 'opened' });
      return !was;
    });
  }, [feel]);

  const [switchSeq, setSwitchSeq] = useState(0);
  const shown = useRef(conversationId);
  useEffect(() => { shown.current = conversationId; }, [conversationId]);
  const opener = useRef<HTMLElement | null>(null);
  const openConversation = useCallback((id: Id, from?: HTMLElement | null) => {
    opener.current = from ?? null;
    if (id !== shown.current) {
      shown.current = id;
      setConversationId(id);
      setSwitchSeq((n) => n + 1);
    }
    setOpen(true);
  }, [setOpen]);

  // Closing hands the focus back to what opened the panel: the "(qua Hana)" link that did (D169, R13), else the
  // header button (the sheet's own return may land on whatever opened it). A link gone from the page falls back too.
  const wasOpen = useRef(open);
  useEffect(() => {
    if (wasOpen.current && !open) {
      const from = opener.current;
      opener.current = null;
      (from?.isConnected === true ? from : buttonRef.current)?.focus();
    }
    wasOpen.current = open;
  }, [open]);

  // ⌘J / Ctrl+J (D86), even from a text field, but not over another dialog.
  useEffect(() => {
    if (!usable) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() !== 'j' || !(e.metaKey || e.ctrlKey) || e.altKey || e.shiftKey) return;
      const modal = document.querySelector<HTMLElement>(MODAL_OPEN);
      // Another dialog owns the keys; the phone sheet is itself a MODAL_OPEN match and holds the panel body.
      if (modal !== null && modal.querySelector('[data-assistant-panel]') === null) return;
      e.preventDefault();                                     // Chrome's Ctrl+J (downloads) inside the app
      toggle();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [usable, toggle]);

  const [asked, setAsked] = useState(0);
  const ask = useCallback((text: string, about?: AssistantObjectRef | { context: AssistantTurnContext }) => {
    if (about === undefined) {
      waiting.current = { text };
    } else if ('context' in about) {
      waiting.current = { text, context: about.context };
    } else {
      const focus = readWorkplaceFocus();
      if (focus !== null && (about.type === 'task' || about.type === 'note')) {
        publishWorkplaceFocus({ ...focus, selectedId: about.id, selectedType: about.type });
      }
      waiting.current = { text, objectRef: { type: about.type, id: about.id } };
    }
    setAsked((n) => n + 1);
    setOpen(true);
  }, [setOpen]);
  const takeAsked = useCallback(() => {
    const next = waiting.current;
    waiting.current = null;
    return next;
  }, []);

  // S8 D137: a command handed over by `/welcome` (`askOnArrival`), asked once and dropped from the history entry, so
  // a reload or "Back" does not ask it again. It waits in `waiting` like any ask until the panel takes it.
  const navigate = useNavigate();
  const handed = (location.state as { assistantAsk?: unknown } | null)?.assistantAsk;
  const handedKey = useRef<string | null>(null);
  useEffect(() => {
    if (typeof handed !== 'string' || !usable || handedKey.current === location.key) return;
    handedKey.current = location.key;
    void navigate({ pathname: location.pathname, search: location.search, hash: location.hash },
      { replace: true, state: null });
    ask(handed);
  }, [handed, usable, location, navigate, ask]);

  // D160: a Map drop or batch jump waits here until the panel (mounted by the open) takes it. Drops queue up (no file
  // is lost to a second drop); a second jump replaces an untaken one. Both are taken by swapping the ref, so the
  // panel's mount effect running twice (StrictMode) starts each drop once.
  const drops = useRef<AssistantDrop[]>([]);
  const [dropped, setDropped] = useState(0);
  const dropFiles = useCallback((files: readonly File[], opts: { anchorItemId?: Id } = {}) => {
    if (files.length === 0) return;
    drops.current.push(opts.anchorItemId === undefined ? { files: [...files] }
      : { files: [...files], anchorItemId: opts.anchorItemId });
    setDropped((n) => n + 1);
    setOpen(true);
  }, [setOpen]);
  const takeDrops = useCallback(() => {
    const next = drops.current;
    drops.current = [];
    return next;
  }, []);
  const [mapIngest, setMapIngest] = useState<{ jobId: Id; seq: number } | null>(null);
  const ingestedFromMap = useCallback((jobId: Id) => {
    setMapIngest((m) => ({ jobId, seq: (m?.seq ?? 0) + 1 }));
  }, []);
  const jumpTo = useRef<IngestJobDTO | null>(null);
  const [shownBatch, setShownBatch] = useState(0);
  const showBatch = useCallback((job: IngestJobDTO) => {
    jumpTo.current = job;
    setShownBatch((n) => n + 1);
    setOpen(true);
  }, [setOpen]);
  const takeShown = useCallback(() => {
    const next = jumpTo.current;
    jumpTo.current = null;
    return next;
  }, []);

  const [trayShown, setTrayShown] = useState(0);
  const showTray = useCallback(() => {
    setTrayShown((n) => n + 1);
    setOpen(true);
  }, [setOpen]);

  const ui = useMemo<AssistantUi | null>(() => (prefs == null ? null : {
    prefs, principal, open, setOpen, toggle, conversationId, setConversationId, openConversation, switchSeq, emotion,
    feel, still: prefs.reducedMotion, buttonRef, hanaUndo, glow, ask, asked, takeAsked, dropFiles, dropped, takeDrops,
    mapIngest, ingestedFromMap, showBatch, shownBatch, takeShown, showTray, trayShown, appOpenSettled, appOpenBubble,
  }), [prefs, principal, open, setOpen, toggle, conversationId, openConversation, switchSeq, emotion, feel, hanaUndo,
    glow, ask, asked, takeAsked, dropFiles, dropped, takeDrops, mapIngest, ingestedFromMap, showBatch, shownBatch,
    takeShown, showTray, trayShown, appOpenSettled, appOpenBubble]);

  // One element type whatever prefs say (S6 gate 6): a Fragment while they load and a Provider once they come made
  // React throw the whole frame away and mount it again a few ms after the first paint, shutting a drawer or inbox
  // opened in between and detaching every node read then. Not usable here is only a null context and no slot.
  const live = usable ? ui : null;
  return (
    <AssistantContext.Provider value={live}>
      {children(live === null ? undefined : {
        open, button: <AssistantButton />, panel: open ? <AssistantPanel /> : null, missions: live.prefs.missionsEnabled,
      })}
    </AssistantContext.Provider>
  );
}
